import React, { useState } from 'react'
import { api } from '../../utils/api'

const RECIPIENTS = [
  { email: 'simon@audiotrader.co.uk', label: 'Simon' },
  { email: 'zsolt@audiotrader.co.uk', label: 'Zsolt' },
  { email: 'benjamin@audiotrader.co.uk', label: 'Benjamin' },
]

function PaymentConfirmationEmail() {
  // --- State ---
  const [dealId, setDealId] = useState('')
  const [dealInfo, setDealInfo] = useState(null)
  const [selectedRecipients, setSelectedRecipients] = useState(new Set(['zsolt@audiotrader.co.uk']))
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [result, setResult] = useState(null)
  const [error, setError] = useState(null)

  // --- Handlers ---
  async function checkDeal() {
    if (!dealId.trim()) return
    setLoading(true)
    setError(null)
    setDealInfo(null)
    setResult(null)
    try {
      const response = await api.get(`/api/deals/${dealId.trim()}/email-check`)
      setDealInfo(response)
    } catch (err) {
      setError(err.message || 'Failed to fetch deal')
    } finally {
      setLoading(false)
    }
  }

  async function sendTestEmail(paymentMethod) {
    const recipients = [...selectedRecipients]
    if (recipients.length === 0) {
      setError('Select at least one recipient')
      return
    }
    setSending(true)
    setError(null)
    setResult(null)
    try {
      const response = await api.post('/api/deals/test-email', {
        dealId: dealId.trim(),
        paymentMethod,
        recipientEmails: recipients
      })
      setResult(response)
      // Update deal info with issue flags from response
      if (response.issueFlags) {
        setDealInfo(prev => prev
          ? { ...prev, issueFlags: response.issueFlags, dealName: response.dealName, amount: response.amount }
          : { dealName: response.dealName, amount: response.amount, issueFlags: response.issueFlags }
        )
      }
    } catch (err) {
      setError(err.message || 'Failed to send test email')
    } finally {
      setSending(false)
    }
  }

  function toggleRecipient(email) {
    const next = new Set(selectedRecipients)
    if (next.has(email)) next.delete(email)
    else next.add(email)
    setSelectedRecipients(next)
  }

  function getTypeBadge(flags) {
    if (!flags) return null
    if (flags.hasConditionChange && flags.hasIssueTrade) return { label: 'Both', className: 'badge-danger' }
    if (flags.hasConditionChange) return { label: 'Condition modified', className: 'badge-warning' }
    if (flags.hasIssueTrade) return { label: 'Issue split', className: 'badge-warning' }
    return { label: 'Clean trade', className: 'badge-success' }
  }

  // --- JSX ---
  return (
    <div>
      <h1 style={{ marginBottom: 'var(--space-6)' }}>Payment Confirmation Email Tester</h1>

      <div className="card" style={{ padding: 'var(--space-6)' }}>
        {/* Deal ID input */}
        <div style={{ display: 'flex', gap: 'var(--space-3)', marginBottom: 'var(--space-6)' }}>
          <input
            type="text"
            className="input"
            placeholder="Enter deal ID"
            value={dealId}
            onChange={e => setDealId(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && checkDeal()}
            style={{ flex: 1 }}
          />
          <button
            className="btn btn-primary"
            onClick={checkDeal}
            disabled={!dealId.trim() || loading}
          >
            {loading ? 'Checking...' : 'Check Deal'}
          </button>
        </div>

        {/* Error display */}
        {error && (
          <div style={{ padding: 'var(--space-3)', background: 'rgba(220,53,69,0.1)', borderRadius: 8, marginBottom: 'var(--space-4)', color: '#dc3545' }}>
            {error}
          </div>
        )}

        {/* Deal info display */}
        {dealInfo && (
          <div style={{ background: 'var(--bg)', padding: 'var(--space-4)', borderRadius: 8, marginBottom: 'var(--space-6)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-2)' }}>
              <strong>{dealInfo.dealName || 'Unknown Deal'}</strong>
              {(() => {
                const badge = getTypeBadge(dealInfo.issueFlags)
                return badge ? (
                  <span className={`badge ${badge.className}`}>
                    {badge.label} — {dealInfo.issueFlags.showReviewLink ? 'Review link included' : 'Review link excluded'}
                  </span>
                ) : null
              })()}
            </div>
            {dealInfo.firstName && (
              <div style={{ color: 'var(--muted)', marginBottom: 'var(--space-1)' }}>
                Contact: {dealInfo.firstName}
              </div>
            )}
            <div style={{ color: 'var(--muted)' }}>
              Amount: {'\u00A3'}{parseFloat(dealInfo.amount || 0).toFixed(2)}
            </div>
          </div>
        )}

        {/* Recipients */}
        <div style={{ marginBottom: 'var(--space-6)' }}>
          <label style={{ fontWeight: 600, marginBottom: 'var(--space-2)', display: 'block' }}>Recipients</label>
          {RECIPIENTS.map(r => (
            <label key={r.email} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={selectedRecipients.has(r.email)}
                onChange={() => toggleRecipient(r.email)}
              />
              {r.email}
            </label>
          ))}
        </div>

        {/* Send buttons */}
        <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
          <button
            className="btn btn-primary"
            onClick={() => sendTestEmail('bank_transfer')}
            disabled={selectedRecipients.size === 0 || sending || !dealId.trim()}
          >
            {sending ? 'Sending...' : 'Send as Bank Transfer'}
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => sendTestEmail('paypal')}
            disabled={selectedRecipients.size === 0 || sending || !dealId.trim()}
          >
            {sending ? 'Sending...' : 'Send as PayPal'}
          </button>
        </div>

        {/* Send result */}
        {result && (
          <div style={{ marginTop: 'var(--space-4)', padding: 'var(--space-4)', background: 'rgba(59,178,115,0.1)', borderRadius: 8 }}>
            <div style={{ fontWeight: 600, color: 'var(--color-success)', marginBottom: 'var(--space-2)' }}>
              {Array.isArray(result.emailsSent) ? result.emailsSent.length : result.emailsSent} email(s) sent successfully
            </div>
            {Array.isArray(result.emailsSent) && result.emailsSent.length > 0 && (
              <div style={{ fontSize: 13, color: 'var(--muted)' }}>
                Sent to: {result.emailsSent.join(', ')}
              </div>
            )}
            {result.issueFlags && (() => {
              const badge = getTypeBadge(result.issueFlags)
              return badge ? (
                <div style={{ marginTop: 'var(--space-2)', fontSize: 13 }}>
                  Trade type: <span className={`badge ${badge.className}`}>{badge.label}</span>
                  {' \u2014 '}
                  {result.issueFlags.showReviewLink ? 'Review link was included' : 'Review link was excluded'}
                </div>
              ) : null
            })()}
            {result.errors?.length > 0 && (
              <div style={{ marginTop: 'var(--space-2)', color: '#dc3545', fontSize: 13 }}>
                Errors: {result.errors.map(e => typeof e === 'string' ? e : e.error || JSON.stringify(e)).join(', ')}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default PaymentConfirmationEmail
